import * as React from 'react'
import { FocusEvent, useCallback, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { Field, Form, useFormikContext } from 'formik'
import { FormFieldsProps } from './typings'
import { useForm, useValidateOnMount } from './hooks'
import { HeaderAddButton, SideDrawerForm } from '@sales-center/components'
import {
  AddressFields,
  BaseTextField,
  MuiCheckboxFormik,
  MuiPhoneFieldFormik,
} from '@user/components/Formik'

import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import {
  EmailCollection,
  useEmailCollection,
} from '@user/components/EmailCollection'
import { AddServiceLocationFormModel } from '.'
import { updatePhoneValueSearch } from '@sales-center/common'
import { useCheckIsMobileNumber } from '@user/store/utilities'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Box from '@mui/material/Box'

const getStyles = (theme: Theme) => ({
  drawer: {
    padding: theme.spacing(2),
    overflow: 'hidden,auto',
    height: 'auto',
  },

  hideRoot: { margin: theme.spacing(0) },
  hideLabel: { fontSize: '16px' },
})

export const FormFields: React.FC<FormFieldsProps> = (props) => {
  const {
    onClose,
    branches,
    locationTypes,
    divisions,
    showAddLabel,
    externalLocation = false,
    matched = false,
    mode,
    newExternalLocation = false,
    showUpdateCheckbox,
    fromAgreement = false,
    runPestPacValidation = false,
  } = props
  const matchedUpdateMode = matched && mode === 'Update'
  useValidateOnMount()
  const {
    isSubmitting,
    submitForm,
    onChangeLocationType,
    onChangeDivision,
    onChangeBranch,
    nameRequired,
    emailRequired,
    phoneRequired,
  } = useForm(props)
  const [hideOptional, setHideOptional] = useState(false)
  const {
    setFieldValue,
    handleChange,
    setFieldTouched,
    values: { phoneNumber, altPhoneNumber, contactTime },
  } = useFormikContext<AddServiceLocationFormModel>()

  const { addEmail, count: emailCount } =
    useEmailCollection<AddServiceLocationFormModel>('emailAddresses')
  const { isMobileNumber: isMobilePhone, execute: checkIsPhoneMobile } =
    useCheckIsMobileNumber(phoneNumber)
  const { isMobileNumber: isMobileAltPhone, execute: checkIsAltPhoneMobile } =
    useCheckIsMobileNumber(altPhoneNumber)

  const addEmailDisabled = emailCount >= 3

  const onBlurPhone = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value, name } = event.target
      const updatedValue = updatePhoneValueSearch(value)
      if (updatedValue == '') {
        setFieldValue(name == 'phoneNumber' ? 'phoneExt' : 'altPhoneExt', '')
      }
      setFieldValue(name, updatedValue)
      if (name == 'phoneNumber') {
        checkIsPhoneMobile(updatedValue)
      } else {
        checkIsAltPhoneMobile(updatedValue)
      }
    },
    [setFieldValue, checkIsPhoneMobile, checkIsAltPhoneMobile],
  )

  const onChangeHideOptional = useCallback(
    (_e, checked: boolean) => setHideOptional(checked),
    [],
  )
  const styles = getStyles(useTheme())
  const containerTop = !matched ? '74px' : matchedUpdateMode ? '165px' : '122px'

  return (
    <SideDrawerForm
      disableSave={isSubmitting}
      onSave={submitForm}
      onCancel={onClose}
      sx={styles.drawer}
      footer={isSubmitting && <LinearProgress color="primary" />}
      containerTop={fromAgreement ? '70px' : containerTop}
    >
      <Form>
        <Grid container spacing={2}>
          {!fromAgreement ? (
            <>
              {showAddLabel && (
                <Grid item xs={12}>
                  <Typography>
                    A new Service Location will be created for the associated
                    bill-to on this Record.
                  </Typography>
                </Grid>
              )}
              {showUpdateCheckbox && (
                <Grid item xs={12}>
                  <Field
                    name="updateBillTo"
                    label="Update Bill-To with this contact Information"
                    component={MuiCheckboxFormik}
                    color="primary"
                    data-test-id="updateBillToCheckbox"
                  />
                </Grid>
              )}
            </>
          ) : (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography
                  sx={{
                    fontSize: '24px',
                    fontWeight: '400',
                    fontHeight: '32px',
                  }}
                >
                  Create Location
                </Typography>
                <FormControlLabel
                  sx={{
                    root: styles.hideRoot,
                    label: styles.hideLabel,
                  }}
                  labelPlacement="end"
                  label={
                    <Typography variant="body1">
                      Display only required fields
                    </Typography>
                  }
                  control={
                    <Switch
                      size="small"
                      checked={hideOptional}
                      onChange={onChangeHideOptional}
                      data-test-id="hideOptionalSwitch"
                    />
                  }
                />
              </Box>

              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: '700',
                  fontHeight: '21px',
                  letterSpacing: '0.15px',
                }}
              >
                Location Information
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Field
              name="branchId"
              label="Branch"
              component={BaseTextField}
              size="small"
              select
              required
              onChange={onChangeBranch}
              disabled={isSubmitting || matchedUpdateMode}
              avoidValidateOnBlur
              data-test-id="branch-field"
            >
              {branches.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Field>
          </Grid>

          {!hideOptional && (
            <Grid item xs={12}>
              <Field
                name="locationTypeId"
                label="Location Type"
                component={BaseTextField}
                size="small"
                select
                onChange={onChangeLocationType}
                disabled={isSubmitting || matchedUpdateMode}
                avoidValidateOnBlur
                data-test-id="location-type-field"
              >
                {locationTypes.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
          )}

          {!hideOptional && (
            <Grid item xs={12}>
              <Field
                name="divisionId"
                label="Division"
                component={BaseTextField}
                size="small"
                select
                onChange={onChangeDivision}
                disabled={isSubmitting || matchedUpdateMode}
                avoidValidateOnBlur
                data-test-id="division-field"
              >
                {divisions.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Field>
            </Grid>
          )}

          {!hideOptional && (
            <Grid item xs={6}>
              <Field
                name="firstName"
                label="First Name"
                component={BaseTextField}
                size="small"
                required={nameRequired}
                avoidValidateOnBlur
                data-test-id="first-name-field"
              />
            </Grid>
          )}

          {!hideOptional && (
            <Grid item xs={6}>
              <Field
                name="lastName"
                label="Last Name"
                component={BaseTextField}
                size="small"
                required={nameRequired}
                avoidValidateOnBlur
                data-test-id="last-name-field"
              />
            </Grid>
          )}

          {!hideOptional && (
            <Grid item xs={12}>
              <Field
                name="company"
                label="Company"
                component={BaseTextField}
                size="small"
                required={nameRequired}
                avoidValidateOnBlur
                data-test-id="company-field"
              />
            </Grid>
          )}

          {(externalLocation || newExternalLocation || !hideOptional) && (
            <AddressFields
              textFieldProps={{ size: 'small' }}
              disabled={isSubmitting || matchedUpdateMode}
              required={
                externalLocation || newExternalLocation || runPestPacValidation
              }
              setFieldTouched={setFieldTouched}
              avoidValidateOnBlur
            />
          )}

          {(emailRequired || !hideOptional) && (
            <Grid item xs={12} style={{ paddingBottom: 0 }}>
              <HeaderAddButton disabled={addEmailDisabled} onClick={addEmail}>
                +Add additional email address
              </HeaderAddButton>
            </Grid>
          )}

          {(emailRequired || !hideOptional) && (
            <Grid item xs={12}>
              <EmailCollection
                name="emailAddresses"
                required={emailRequired}
                avoidValidateOnBlur
              />
            </Grid>
          )}

          {(phoneRequired || !hideOptional) && (
            <Grid item xs={!hideOptional ? 9 : 12}>
              <Field
                name="phoneNumber"
                label="Phone"
                component={MuiPhoneFieldFormik}
                size="small"
                margin="none"
                required={phoneRequired}
                onBlur={onBlurPhone}
                helperText={isMobilePhone ? 'Number entered is Mobile' : ''}
              />
            </Grid>
          )}

          {!hideOptional && (
            <Grid item xs={3}>
              <Field
                name="phoneExt"
                label="Ext"
                component={BaseTextField}
                size="small"
                disabled={!phoneNumber}
                data-test-id="phone-ext-field"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  setFieldTouched('phoneExt', true)
                  setFieldValue('phoneExt', value)
                }}
              />
            </Grid>
          )}

          {!hideOptional && (
            <Grid item xs={9}>
              <Field
                name="altPhoneNumber"
                label="Alternate Phone"
                component={MuiPhoneFieldFormik}
                margin="none"
                size="small"
                onBlur={onBlurPhone}
                helperText={isMobileAltPhone ? 'Number entered is Mobile' : ''}
                data-test-id="alt-phone-field"
              />
            </Grid>
          )}

          {!hideOptional && (
            <Grid item xs={3}>
              <Field
                name="altPhoneExt"
                label="Ext"
                component={BaseTextField}
                size="small"
                disabled={!altPhoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  setFieldTouched('altPhoneExt', true)
                  setFieldValue('altPhoneExt', value)
                }}
                data-test-id="alt-phone-ext-field"
              />
            </Grid>
          )}

          {!hideOptional && !fromAgreement && (
            <Grid item xs={12}>
              <Field
                name="contactMethod"
                label="Best Contact Method"
                component={BaseTextField}
                select
                data-test-id="best-contact-method-field"
              >
                <MenuItem value="" disabled>
                  Select Best Contact Method
                </MenuItem>
                <MenuItem value="Phone">Phone</MenuItem>
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
              </Field>
            </Grid>
          )}

          {!hideOptional && !fromAgreement && (
            <Grid item xs={12}>
              <Field
                name="contactTime"
                label="Best Contact Time(s)"
                component={BaseTextField}
                select
                SelectProps={{
                  multiple: true,
                  value: contactTime,
                  onChange: handleChange,
                }}
                data-test-id="best-contact-time-field"
              >
                <MenuItem value="" disabled>
                  Select One or More Times
                </MenuItem>
                <MenuItem value="morning">Morning</MenuItem>
                <MenuItem value="afternoon">Afternoon</MenuItem>
                <MenuItem value="evening">Evening</MenuItem>
                <MenuItem value="weekend">Weekend</MenuItem>
              </Field>
            </Grid>
          )}
        </Grid>
      </Form>
    </SideDrawerForm>
  )
}
FormFields.displayName = 'FormFields'
